import { IdeaStates } from "../constants/feedback";
import { ResourceStrings } from "../ResourceStrings";
import { FeedbackStateConfigMap, IdeaStateConfig } from "./feedbackStateConfig";
import { instance } from "./serverConfig";

export const ideaStates: FeedbackStateConfigMap<IdeaStateConfig> = {
  [IdeaStates.closed]: {
    categoryLabel: "Closed",
    label: ResourceStrings.ideaStateClosed(),
    description: "",  // @TODO: need text for this
    backgroundColor: "#605E5C",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: false,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.duplicate]: {
    categoryLabel: "Closed",
    subLabel: "Duplicate",
    label: ResourceStrings.ideaStateDuplicate(),
    description: instance.isInternal ? "Closed - Duplicate indicates that your suggestion has already been reported by another user. We will provide you a link to the existing feature request." :
        "Closed - Duplicate indicates that someone else has already suggested the same feature. Review the comments to find the link to the existing feature suggestion. Votes and comments have been merged in the original suggestion. Follow the original suggestion.",
    backgroundColor: "#605E5C",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: true,
    returnVotes: false,
  },
  [IdeaStates.notEnoughInfo]: {
    categoryLabel: "Closed",
    subLabel: "Not enough information",
    label: ResourceStrings.ideaStateNotEnoughInfo(),
    description: instance.isInternal ? "Closed - Not Enough Info indicates that we do not have enough information to move forward with your suggestion. We will automatically move a feature request to this state 7 days after marking it Need More Info if we have not received additional information. We can reconsider your feature request after the necessary information is provided." :
        "Closed - Not Enough Info indicates that after several attempts, we haven't received enough information to understand your feature suggestion fully. We have to close the new feature suggestion as we're unable to take any further action at this stage.",
    backgroundColor: "#605E5C",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.otherProduct]: {
    categoryLabel: "Closed",
    subLabel: "Other product",
    label: ResourceStrings.ideaStateOtherProduct(),
    description: instance.isInternal ? "Closed - Other Product indicates that your suggestion applies to another internal engineering system tool." :
        "Closed - Other Product means we're unable to address your feature suggestion at this time because it's not applicable to the product it was reported for. However, we'll provide details on where you can share your new feature suggestion for the appropriate product.",
    backgroundColor: "#605E5C",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: true,
    returnVotes: false,
  },
  [IdeaStates.outOfScope]: {
    categoryLabel: "Closed",
    subLabel: "Out of scope",
    label: ResourceStrings.ideaStateOutOfScope(),
    description: "Closed - Out of Scope If a suggestion doesn’t match our overall product direction, we will close it as Out of Scope. For example, we may have similar investments in other members of the Visual Studio family of products. Or the feature suggested might only be relevant to a few people, making an extension better suited to provide it.",
    backgroundColor: "#605E5C",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.pending]: {
    categoryLabel: "Completed - Preview",
    label: ResourceStrings.ideaStatePending(),
    description: "Completed - Preview indicates that we implemented the feature you suggested. You can download a preview version of Visual Studio that contains your suggestion using the link provided in the comments.",
    backgroundColor: "#197B1B",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: false,
    isFixedState: true,
    votesLocked: false,
    cancelVotesNotAllowed: false,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.fixed]: {
    categoryLabel: "Completed - Release",
    label: ResourceStrings.ideaStateFixed(),
    description: "Completed - Release indicates that your new feature suggestion has been released in the latest product update. The Visual Studio update can be downloaded using the link provided in the comments.",
    backgroundColor: "#197B1B",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    isFixedState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.moreInfo]: {
    categoryLabel: "Need More Info",
    label: ResourceStrings.ideaStateMoreInfo(),
    description: instance.isInternal ? "Need More Info indicates we need more information from you in order to move forward with the suggestion." :
        "A feature suggestion marked Need More Info means that we need more details so we can better understand your suggestion. Check the comments, where we'll ask for additional information to get a deeper understanding.",
    backgroundColor: "#D63C18",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: false,
    votesLocked: false,
    cancelVotesNotAllowed: false,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.new]: {
    categoryLabel: "New",
    label: ResourceStrings.ideaStateNew(),
    description: instance.isInternal ? "New indicates that the feature request is newly reported, and no action has been taken on it yet." :
        "New means the suggestion has been newly reported from you or someone else. No action has been taken on it yet. The front line will do some preliminary checks to make sure we can proceed further. Expect to hear from us in about five business days with our next steps.",
    backgroundColor: "#106EBE",
    textColor: "#FFFFFF",
    isInitialState: true,
    isFinalState: false,
    votesLocked: false,
    cancelVotesNotAllowed: false,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.investigation]: {
    categoryLabel: "On Roadmap",
    label: ResourceStrings.ideaStateInvestigation(),
    description: "On Roadmap means that your feature suggestion has a broad community impact and will help improve the product experience. We've allocated time for it on our roadmap. We'll update you on the progress.",
    backgroundColor: "#985A00",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: false,
    votesLocked: false,
    cancelVotesNotAllowed: false,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.consideration]: {
    categoryLabel: "Under Review",
    label: ResourceStrings.ideaStateConsideration(),
    description: "Under Review indicates that the feature suggestion has been queued up for prioritization. We prioritize features to bring our broader developer community the best value, also taking the product roadmap into consideration.",
    backgroundColor: "#106EBE",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: false,
    votesLocked: false,
    cancelVotesNotAllowed: false,
    commentsLocked: false,
    returnVotes: false,
  },
  [IdeaStates.invalid]: {
    categoryLabel: "Invalid State",
    label: "Invalid State",
    description: "The state of this feedback is invalid. Please log a bug with the VS Feedback team.",
    backgroundColor: "#000000",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: false,
    returnVotes: false,
  },
  // OXO specific states  
  [IdeaStates.active]: {
    categoryLabel: "Active",
    label: "Active",
    description: instance.isInternal ? "Active indicates we have launched work associated with your suggestion." :
        "Active state indicate feature request is under consideration and final decision is pending.",
    backgroundColor: "#106EBE",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: false,
    votesLocked: false,
    cancelVotesNotAllowed: false,
    commentsLocked: false,
    returnVotes: false,
  },  
  [IdeaStates.closedObsolete]: {
    categoryLabel: "Closed Obsolete",
    label: "Closed Obsolete",
    description: instance.isInternal ? "Closed - Obsolete indicates that your suggestion relates to a system or process that is no longer exists or is no longer supported." :
        "This state indicate feature request is obsolete.",
    backgroundColor: "#197B1B",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: false,
    returnVotes: false,
  },  
  [IdeaStates.completed]: {
    categoryLabel: "Completed",
    label: "Completed",
    description: instance.isInternal ? "Completed indicates we implemented the feature you suggested." :
        "This state indicate feature request is complete.",
    backgroundColor: "#197B1B",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: false,
    returnVotes: false,
  },  
  [IdeaStates.onBacklog]: {
    categoryLabel: "On Backlog",
    label: "On Backlog",
    description: instance.isInternal ? "On Backlog indicates that we are unable to immediately prioritize your feature request currently. This will change based on a combination of the queue of outstanding requests, priority, team resourcing, and upvotes from other users." :
        "This state indicate feature request was added to backlog.",
    backgroundColor: "#197B1B",
    textColor: "#FFFFFF",
    isInitialState: false,
    isFinalState: true,
    votesLocked: true,
    cancelVotesNotAllowed: true,
    commentsLocked: false,
    returnVotes: false,
  },
};